import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth, upgradeEvents } from '../context/AuthContext';
import { formatCurrency, formatDate, getPaymentStatusColor } from '../utils/helpers';
import Loading from '../components/Loading';
import Alert from '../components/Alert';

import { 
  LayoutDashboard, 
  TrendingUp, 
  Receipt, 
  CheckCircle2, 
  AlertCircle, 
  Clock,
  Calendar,
  BarChart3,
  LineChart 
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startingTrial, setStartingTrial] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('sales');
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    fetchDashboardData();
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, invoicesRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/recent-invoices?limit=10')
      ]);

      setStats(statsRes.data.stats);
      setRecentInvoices(invoicesRes.data.invoices || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setStartingTrial(true);
      const res = await api.post('/auth/activate-trial');
      if (res.data.success) {
        alert('7-Day Free Trial activated successfully! Enjoy the Starter Plan.');
        window.location.reload();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start trial');
      setStartingTrial(false);
    }
  };

  const formatShortCurrency = (val) => {
    if (val >= 100000) {
      return `₹${(val / 100000).toFixed(1).replace(/\.0$/, '')}L`;
    }
    if (val >= 1000) {
      return `₹${(val / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    }
    return `₹${Math.round(val)}`;
  };

  if (loading) return <Loading />;

  // Trend Data Calculations
  const trendData = stats?.monthlyTrend || [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const chartData = trendData.map(item => ({
    label: `${monthNames[item._id.month - 1]} ${item._id.year}`,
    sales: item.totalSales || 0,
    gst: item.totalGST || 0,
    count: item.count || 0
  }));

  const maxSales = Math.max(...chartData.map(d => d.sales), 1000);

  const svgWidth = 600;
  const svgHeight = 220;
  const paddingLeft = 55;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 30;

  const graphWidth = svgWidth - paddingLeft - paddingRight;
  const graphHeight = svgHeight - paddingTop - paddingBottom;

  const points = chartData.map((d, index) => {
    const sectionWidth = graphWidth / chartData.length;
    const x = paddingLeft + (index * sectionWidth) + (sectionWidth / 2);
    const h = (d.sales / maxSales) * graphHeight;
    const y = paddingTop + graphHeight - h;
    return { x, y, h, data: d };
  });

  const colWidth = chartData.length > 0 ? graphWidth / chartData.length : graphWidth;

  const gridCount = 4;
  const gridLines = Array.from({ length: gridCount }).map((_, i) => {
    const ratio = i / (gridCount - 1);
    const y = paddingTop + ratio * graphHeight;
    const value = maxSales - ratio * maxSales;
    return { y, value };
  });


  // Payment Breakdown Calculations
  const breakdown = stats?.paymentStatusBreakdown || [];
  const paymentData = {
    paid: breakdown.find(b => b._id === 'paid') || { amount: 0, count: 0 },
    partial: breakdown.find(b => b._id === 'partial') || { amount: 0, count: 0 },
    due: breakdown.find(b => b._id === 'due') || { amount: 0, count: 0 }
  };

  const totalBreakdownAmount = paymentData.paid.amount + paymentData.partial.amount + paymentData.due.amount;
  const paidPct = totalBreakdownAmount > 0 ? (paymentData.paid.amount / totalBreakdownAmount) * 100 : 0;
  const partialPct = totalBreakdownAmount > 0 ? (paymentData.partial.amount / totalBreakdownAmount) * 100 : 0;
  const duePct = totalBreakdownAmount > 0 ? (paymentData.due.amount / totalBreakdownAmount) * 100 : 0;

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px' }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
            <LayoutDashboard size={28} /> Dashboard
          </h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0, fontSize: '15px' }}>
            {getGreeting()}, <strong style={{ color: 'var(--text-primary)' }}>{user?.name || 'Admin'}</strong>
          </p>
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'var(--gray-50, #f8fafc)',
          padding: '8px 16px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <Clock size={18} style={{ color: 'var(--accent)' }} />
          <span style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
            {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', marginLeft: '8px', paddingLeft: '8px', borderLeft: '1px solid var(--border-color)' }}>
            {currentTime.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
          </span>
        </div>
      </div>

      {!user?.hasUsedTrial && user?.planType === 'starter' && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)',
          padding: '24px',
          borderRadius: '12px',
          color: '#fff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 'bold' }}>Start Your 7-Day Free Trial! ✨</h3>
            <p style={{ margin: 0, opacity: 0.9 }}>Get full access to the Starter Plan completely free for 7 days. No credit card required.</p>
          </div>
          <button 
            onClick={handleStartTrial}
            disabled={startingTrial}
            style={{
              background: '#fff',
              color: '#ef4444',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: startingTrial ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              opacity: startingTrial ? 0.7 : 1
            }}
          >
            {startingTrial ? 'Activating...' : 'Activate Free Trial'}
          </button>
        </div>
      )}

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {stats && (
        <>
          {/* Today's Stats */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={20} /> Today's Performance
              </div>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">Total Sales</div>
                  <TrendingUp size={20} style={{ color: '#38bdf8', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.today.totalSales || 0)}</div>
                <div className="stat-label">{stats.today.count || 0} Invoices</div>
              </div>
              <div className="stat-card success">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">GST Collected</div>
                  <Receipt size={20} style={{ color: '#10b981', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.today.totalGST || 0)}</div>
              </div>
              <div className="stat-card success">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">Amount Paid</div>
                  <CheckCircle2 size={20} style={{ color: '#10b981', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.today.totalPaid || 0)}</div>
              </div>
              <div className="stat-card danger">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">Amount Due</div>
                  <AlertCircle size={20} style={{ color: '#f43f5e', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.today.totalDue || 0)}</div>
              </div>
            </div>
          </div>

          {/* Large Analytics Card */}
          {user?.planType === 'starter' ? (
            <div className="card" style={{ 
              position: 'relative', 
              padding: '60px 40px', 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, rgba(7,147,167,0.03) 0%, rgba(15,159,140,0.08) 100%)',
              border: '1px solid rgba(7,147,167,0.15)', 
              borderRadius: 'var(--radius-xl)',
              overflow: 'hidden'
            }}>
              {/* Abstract Background Elements */}
              <div style={{ position: 'absolute', top: '-20%', left: '-5%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(7,147,167,0.08) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', bottom: '-20%', right: '-5%', width: '250px', height: '250px', background: 'radial-gradient(circle, rgba(15,159,140,0.08) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%', pointerEvents: 'none' }} />
              
              <div style={{ position: 'relative', zIndex: 2 }}>
                {/* Miniature CSS Animated Bar Chart */}
                <div style={{ 
                  width: '140px', height: '80px', margin: '0 auto 30px auto', 
                  display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '10px',
                  borderBottom: '2px solid rgba(7,147,167,0.15)',
                  paddingBottom: '4px'
                }}>
                  <div style={{ width: '22px', height: '40%', background: 'linear-gradient(to top, var(--accent) 0%, rgba(7,147,167,0.3) 100%)', borderRadius: '4px 4px 0 0', animation: 'grow-up 1.2s ease-in-out infinite alternate', animationDelay: '0s', opacity: 0.8 }} />
                  <div style={{ width: '22px', height: '70%', background: 'linear-gradient(to top, var(--accent) 0%, rgba(7,147,167,0.5) 100%)', borderRadius: '4px 4px 0 0', animation: 'grow-up 1.2s ease-in-out infinite alternate', animationDelay: '0.2s', opacity: 0.85 }} />
                  <div style={{ width: '22px', height: '50%', background: 'linear-gradient(to top, var(--accent) 0%, rgba(7,147,167,0.7) 100%)', borderRadius: '4px 4px 0 0', animation: 'grow-up 1.2s ease-in-out infinite alternate', animationDelay: '0.4s', opacity: 0.9 }} />
                  <div style={{ width: '22px', height: '100%', background: 'linear-gradient(to top, var(--accent) 0%, #0f9f8c 100%)', borderRadius: '4px 4px 0 0', animation: 'grow-up 1.2s ease-in-out infinite alternate', animationDelay: '0.6s', boxShadow: '0 0 15px rgba(15,159,140,0.4)' }} />
                </div>
                
                <h3 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '12px', color: 'var(--text-primary)', letterSpacing: '-0.5px' }}>
                  Unlock Advanced Analytics
                </h3>
                
                <p style={{ color: 'var(--text-secondary)', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px', maxWidth: '480px', margin: '0 auto 32px auto' }}>
                  Get deep insights into your revenue, track outstanding payments, and monitor business growth with our powerful reporting tools.
                </p>
                
                <button 
                  onClick={() => upgradeEvents.emit({ feature: 'advancedAnalytics', requiredPlan: 'professional', message: 'Get deep insights into your revenue, track outstanding payments, and monitor business growth with our powerful reporting tools.' })}
                  className="btn" style={{ 
                  display: 'inline-flex', 
                  padding: '14px 32px', 
                  background: 'linear-gradient(90deg, var(--accent) 0%, #0f9f8c 100%)', 
                  color: 'white',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 10px 25px rgba(7,147,167,0.25)',
                  transition: 'all 0.3s ease',
                  border: 'none',
                  cursor: 'pointer'
                }}>
                  Upgrade to Professional
                </button>
              </div>
            </div>
          ) : (
          <>
          <div className="card" style={{ position: 'relative' }}>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BarChart3 size={20} /> Analytics Overview
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  onClick={() => setActiveTab('sales')}
                  className={`btn btn-sm ${activeTab === 'sales' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '4px 12px', fontSize: '13px' }}
                >
                  Sales Trend
                </button>
                <button 
                  onClick={() => setActiveTab('payment')}
                  className={`btn btn-sm ${activeTab === 'payment' ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '4px 12px', fontSize: '13px' }}
                >
                  Payment Status
                </button>
              </div>
            </div>

            <div className="card-body" style={{ minHeight: '260px', padding: '20px 0 0', position: 'relative' }}>
              {activeTab === 'sales' ? (
                chartData.length > 0 ? (
                  <div style={{ position: 'relative', width: '100%' }}>
                    <svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} width="100%" height="100%">
                      <defs>
                        <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.9"/>
                          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.2"/>
                        </linearGradient>
                        <linearGradient id="barHoverGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--accent)" stopOpacity="1"/>
                          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.4"/>
                        </linearGradient>
                        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                          <feGaussianBlur stdDeviation="2" result="blur" />
                          <feComposite in="SourceGraphic" in2="blur" operator="over" />
                        </filter>
                      </defs>

                      {/* Grid Lines */}
                      {gridLines.map((g, i) => (
                        <g key={i}>
                          <line 
                            x1={paddingLeft} 
                            y1={g.y} 
                            x2={svgWidth - paddingRight} 
                            y2={g.y} 
                            stroke="rgba(0,0,0,0.06)" 
                            strokeDasharray="4 4" 
                          />
                          <text 
                            x={paddingLeft - 10} 
                            y={g.y + 4} 
                            textAnchor="end" 
                            fontSize="10" 
                            fill="var(--text-secondary)"
                          >
                            {formatShortCurrency(g.value)}
                          </text>
                        </g>
                      ))}

                      {/* Bar Chart */}
                      {points.map((p, i) => {
                        const barW = Math.min(colWidth * 0.4, 40);
                        const isHovered = hoveredIndex === i;
                        return (
                          <g key={i}>
                            <rect
                              x={p.x - barW / 2}
                              y={p.y}
                              width={barW}
                              height={p.h}
                              fill={isHovered ? "url(#barHoverGradient)" : "url(#barGradient)"}
                              rx="4"
                              ry="4"
                              style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
                              filter={isHovered ? "url(#glow)" : ""}
                              onMouseEnter={() => setHoveredIndex(i)}
                              onMouseLeave={() => setHoveredIndex(null)}
                            />
                            {/* Hover Value Tooltip Indicator */}
                            {isHovered && (
                              <text 
                                x={p.x} 
                                y={p.y - 10} 
                                textAnchor="middle" 
                                fontSize="10" 
                                fontWeight="bold" 
                                fill="var(--text-primary)"
                              >
                                {formatShortCurrency(p.data.sales)}
                              </text>
                            )}
                          </g>
                        );
                      })}

                      {/* X Axis Labels */}
                      {points.map((p, i) => (
                        <text 
                          key={i} 
                          x={p.x} 
                          y={paddingTop + graphHeight + 18} 
                          textAnchor="middle" 
                          fontSize="9" 
                          fill="var(--text-secondary)"
                        >
                          {p.data.label.split(' ')[0]}
                        </text>
                      ))}

                      {/* Hover Interaction Areas for entire column */}
                      {points.map((p, i) => (
                        <rect
                          key={`hover-${i}`}
                          x={p.x - colWidth / 2}
                          y={paddingTop}
                          width={colWidth}
                          height={graphHeight}
                          fill="transparent"
                          onMouseEnter={() => setHoveredIndex(i)}
                          onMouseLeave={() => setHoveredIndex(null)}
                          style={{ cursor: 'pointer' }}
                        />
                      ))}
                    </svg>

                    {/* Tooltip */}
                    {hoveredIndex !== null && points[hoveredIndex] && (
                      <div style={{
                        position: 'absolute',
                        left: `${(points[hoveredIndex].x / svgWidth) * 100}%`,
                        top: `${(points[hoveredIndex].y / svgHeight) * 100 - 20}%`,
                        transform: 'translate(-50%, -100%)',
                        background: 'rgba(17, 24, 39, 0.95)',
                        color: '#ffffff',
                        padding: '10px 14px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        pointerEvents: 'none',
                        zIndex: 100,
                        boxShadow: 'var(--shadow-lg)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        lineHeight: '1.4'
                      }}>
                        <div style={{ fontWeight: '700', marginBottom: '6px', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '3px' }}>
                          {points[hoveredIndex].data.label}
                        </div>
                        <div>Sales: <strong>{formatCurrency(points[hoveredIndex].data.sales)}</strong></div>
                        <div>GST: <strong>{formatCurrency(points[hoveredIndex].data.gst)}</strong></div>
                        <div>Invoices: <strong>{points[hoveredIndex].data.count}</strong></div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: 'var(--text-secondary)' }}>
                    No sales trend data available yet.
                  </div>
                )
              ) : (
                totalBreakdownAmount > 0 ? (
                  <div style={{ padding: '10px 20px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px' }}>
                      Revenue Breakdown by Invoice Payment Status
                    </div>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                      Visualizing total generated revenue ({formatCurrency(totalBreakdownAmount)}) across paid, partially paid, and due status.
                    </div>
                    {/* Horizontal Stacked Bar */}
                    <div style={{
                      height: '28px',
                      display: 'flex',
                      borderRadius: '14px',
                      overflow: 'hidden',
                      background: 'var(--gray-100)',
                      marginBottom: '30px',
                      border: '1px solid var(--border)'
                    }}>
                      {paidPct > 0 && (
                        <div style={{ width: `${paidPct}%`, background: '#10b981', transition: 'width 0.3s' }} title={`Paid: ${paidPct.toFixed(1)}%`} />
                      )}
                      {partialPct > 0 && (
                        <div style={{ width: `${partialPct}%`, background: '#f59e0b', transition: 'width 0.3s' }} title={`Partial: ${partialPct.toFixed(1)}%`} />
                      )}
                      {duePct > 0 && (
                        <div style={{ width: `${duePct}%`, background: '#f43f5e', transition: 'width 0.3s' }} title={`Due: ${duePct.toFixed(1)}%`} />
                      )}
                    </div>

                    {/* Stats Legend Grid */}
                    <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                      <div className="stat-card success" style={{ borderLeft: '4px solid #10b981', padding: '16px' }}>
                        <div className="stat-label" style={{ color: '#10b981' }}>Paid Invoices</div>
                        <div className="stat-value" style={{ fontSize: '22px', margin: '6px 0', fontWeight: '700' }}>{formatCurrency(paymentData.paid.amount)}</div>
                        <div className="stat-label">{paymentData.paid.count} Invoices ({paidPct.toFixed(1)}%)</div>
                      </div>
                      <div className="stat-card warning" style={{ borderLeft: '4px solid #f59e0b', padding: '16px' }}>
                        <div className="stat-label" style={{ color: '#f59e0b' }}>Partially Paid</div>
                        <div className="stat-value" style={{ fontSize: '22px', margin: '6px 0', fontWeight: '700' }}>{formatCurrency(paymentData.partial.amount)}</div>
                        <div className="stat-label">{paymentData.partial.count} Invoices ({partialPct.toFixed(1)}%)</div>
                      </div>
                      <div className="stat-card danger" style={{ borderLeft: '4px solid #f43f5e', padding: '16px' }}>
                        <div className="stat-label" style={{ color: '#f43f5e' }}>Amount Due</div>
                        <div className="stat-value" style={{ fontSize: '22px', margin: '6px 0', fontWeight: '700' }}>{formatCurrency(paymentData.due.amount)}</div>
                        <div className="stat-label">{paymentData.due.count} Invoices ({duePct.toFixed(1)}%)</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px', color: 'var(--text-secondary)' }}>
                    No payment distribution data available yet.
                  </div>
                )
              )}
            </div>
          </div>

          {/* Month's Stats */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BarChart3 size={20} /> This Month's Performance
              </div>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">Total Sales</div>
                  <TrendingUp size={20} style={{ color: '#38bdf8', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.month.totalSales || 0)}</div>
                <div className="stat-label">{stats.month.count || 0} Invoices</div>
              </div>
              <div className="stat-card success">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">GST Collected</div>
                  <Receipt size={20} style={{ color: '#10b981', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.month.totalGST || 0)}</div>
              </div>
              <div className="stat-card success">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">Amount Paid</div>
                  <CheckCircle2 size={20} style={{ color: '#10b981', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.month.totalPaid || 0)}</div>
              </div>
              <div className="stat-card danger">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">Amount Due</div>
                  <AlertCircle size={20} style={{ color: '#f43f5e', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.month.totalDue || 0)}</div>
              </div>
            </div>
          </div>

          {/* Year Stats */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <LineChart size={20} /> This Year's Performance
              </div>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">Total Sales</div>
                  <TrendingUp size={20} style={{ color: '#38bdf8', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.year.totalSales || 0)}</div>
                <div className="stat-label">{stats.year.count || 0} Invoices</div>
              </div>
              <div className="stat-card success">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div className="stat-label">Total GST Collected</div>
                  <Receipt size={20} style={{ color: '#10b981', opacity: 0.8 }} />
                </div>
                <div className="stat-value">{formatCurrency(stats.year.totalGST || 0)}</div>
              </div>
              {stats.overdueInvoices > 0 && (
                <div className="stat-card danger">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="stat-label">Overdue Invoices</div>
                    <Clock size={20} style={{ color: '#f43f5e', opacity: 0.8 }} />
                  </div>
                  <div className="stat-value">{stats.overdueInvoices}</div>
                </div>
              )}
            </div>
          </div>
          </>
          )}

          {/* Recent Invoices */}
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Recent Invoices</span>
              <Link to="/invoice" className="btn btn-secondary btn-sm" style={{ padding: '6px 12px', fontSize: '13px' }}>
                View All Invoices
              </Link>
            </div>
            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Invoice No</th>
                    <th>Date</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Payment Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInvoices.length > 0 ? (
                    recentInvoices.map(invoice => (
                      <tr key={invoice._id}>
                        <td style={{ fontWeight: '600' }}>{invoice.invoiceNumber}</td>
                        <td>{formatDate(invoice.invoiceDate)}</td>
                        <td>{invoice.customerDetails?.name || invoice.customer?.name || 'N/A'}</td>
                        <td style={{ fontWeight: '600', color: '#10b981' }}>{formatCurrency(invoice.grandTotal)}</td>
                        <td>
                          <span className={`badge badge-${getPaymentStatusColor(invoice.paymentStatus)}`}>
                            {invoice.paymentStatus.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <Link to={`/invoice/${invoice._id}`} className="btn btn-secondary btn-small" style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 8px', fontSize: '12px' }}>
                            View
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No recent invoices found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </>
      )}
    </div>
  );
};

export default Dashboard;
